import React from "react";
import Form from "react-bootstrap/Form";
import PropTypes from "prop-types";
import { Formik } from "formik";
import * as yup from "yup";
import { ToBase64 } from "../../../../services/api/file-api";
import "./admin-department-create-panel.scss";
import AdminDepartmentFormLeftColumnNew from "./admin-department-form-left-column-new";
import AdminDepartmentFormRightColumnNew from "./admin-department-form-right-column-new";
import AdminDepartmentFormBottomColumnNew from "./admin-department-form-bottom-column-new";

const NAME_REGEX = /^[^~`!@#$%^*(){}:;"<>?/[\]\|\\]*$/i;

const AdminDepartmentFormNew = ({
  department,
  setAlertConfig,
  setPanelVisibility,
  saveDepartment,
  isEdit,
}) => {

  const onDepartmentPictureUploaded = async (e) => {
    let file = e.target.files[0];
    const validImageTypes = [
      "image/jpeg",
      "image/png",
      "image/bmp",
      "image/tiff",
      "image/gif",
    ];

    if (!validImageTypes.includes(file.type)) {
      setAlertConfig((prev) => ({
        ...prev,
        showAlert: true,
        alertAction: "Invalid Picture Format",
        alertMessage: "Image must be either jpg, png, bmp, tiff, or gif.",
        alertVariant: "danger",
      }));
      return;
    } else {
      // check file size
      if (file.size > 2000000) {
        setAlertConfig((prev) => ({
          ...prev,
          showAlert: true,
          alertAction: "File size too large.",
          alertMessage: "Image must be less than 2MB.",
          alertVariant: "danger",
        }));
        return;
      }

      // Browsers cannot decode TIFF (no <img>/data-URL support), so a raw TIFF
      // data URL renders as a gray/broken preview — and the backend re-saves
      // uploads keeping the source format's bytes, so the persisted logo would
      // stay gray after saving too (TAD-5781 follow-up). Convert TIFF to PNG
      // client-side so every downstream consumer — preview and API — receives
      // a PNG data URL. Dynamic import keeps the decoder out of the main chunk.
      if (file.type === "image/tiff") {
        try {
          const { tiffToPngDataUrls } = await import(
            "../../../../util/tiff-helpers"
          );
          const buffer = await file.arrayBuffer();
          // A logo is single-image; decode only the first page.
          const [dataUrl] = tiffToPngDataUrls(buffer, 1);

          // The 2MB cap above was checked against the source TIFF, but the
          // canvas-re-encoded PNG is what actually gets stored and sent to the
          // API, and re-encoding can inflate it. Measure the base64 payload
          // only (excluding the "data:image/png;base64," prefix); decoded
          // bytes are ~3/4 of its length.
          const base64Payload = dataUrl.slice(dataUrl.indexOf(",") + 1);
          if (base64Payload.length * 0.75 > 2000000) {
            setAlertConfig((prev) => ({
              ...prev,
              showAlert: true,
              alertAction: "File size too large.",
              alertMessage: "Image must be less than 2MB.",
              alertVariant: "danger",
            }));
            return;
          }

          return dataUrl;
        } catch (error) {
          setAlertConfig((prev) => ({
            ...prev,
            showAlert: true,
            alertAction: "Invalid Picture Format",
            alertMessage:
              "The TIFF image could not be processed. Please try a different image.",
            alertVariant: "danger",
          }));
          return;
        }
      }

      return await ToBase64(file);
    }
  };

  const onKeyDown = (keyEvent) => {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      keyEvent.preventDefault();
    }
  };

  return (
    <Formik
      key={department.Isloading}
      validationSchema={yup.object({
        Name: yup
          .string()
          .max(50, "Department name must be under 50 characters")
          .matches(
            NAME_REGEX,
            "Please do not enter any special characters in department name"
          )
          .required("Department Name Required"),
        Address: yup.string().nullable(),
        Country: yup.string().required("Country Required"),
        State: yup.string().nullable(),
        City: yup.string().nullable(),
        Zip: yup.string().nullable(),
        WebsiteURL: yup.string().nullable(),
        Active: yup.boolean().required(),
        IsAppointmentEnable: yup.boolean(),
        ExchangeServerUserName: yup.string().nullable(),
        ExchangeServerPassword: yup.string().nullable(),
        ExchangeServerURL: yup.string().nullable(),
      })}
      enableReinitialize
      onSubmit={(values, actions) => {
        saveDepartment(values, actions);
      }}
      initialValues={{
        ...department.data,
      }}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        touched,
        errors,
        setFieldValue,
        setFieldTouched,
        isSubmitting,
        submitForm,
      }) => (
        <Form
          className="department-creation-container"
          onSubmit={handleSubmit}
          onKeyDown={onKeyDown}
        >
          {/* Name, Address, City, State, Country, Postal Code, Date Format, Website URL, Super Admin Status */}
          <AdminDepartmentFormLeftColumnNew
            handleChange={handleChange}
            handleBlur={handleBlur}
            values={values}
            touched={touched}
            errors={errors}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
          />
          {/* Company Logo, Exchange Server Enabled, Exchange Server Username, Exchange Server Password, Exchange Server URL, Department Status, Date Format, Website URL, Super Admin Status */}
          <AdminDepartmentFormRightColumnNew
            handleChange={handleChange}
            values={values}
            errors={errors}
            setFieldValue={setFieldValue}
            isEdit={isEdit}
            onDepartmentPictureUploaded={onDepartmentPictureUploaded}
          />
          {/* Save/Create, Cancel */}
          <AdminDepartmentFormBottomColumnNew
            submitForm={submitForm}
            isSubmitting={isSubmitting}
            setPanelVisibility={setPanelVisibility}
            isEdit={isEdit}
          />
        </Form>
      )}
    </Formik>
  );
};

AdminDepartmentFormNew.propTypes = {
  department: PropTypes.object.isRequired,
  setAlertConfig: PropTypes.func.isRequired,
  setPanelVisibility: PropTypes.func.isRequired,
  saveDepartment: PropTypes.func.isRequired,
  isEdit: PropTypes.bool.isRequired,
};
export default AdminDepartmentFormNew;
